import { SessionService } from '../../../store/services/session.service';
import { openDialog } from '../../../store/actions/page.action';
import { IToggleLeftMenuPayload, PageState, TOGGLE_LEFT_MENU_TYPE } from 'app/store/models/page.model';
import { getUserSession, logout, popFlash } from '../../../store/actions/user.action';
import {
  BILLING_STATUS_ACTIVE,
  BILLING_STATUS_AWAITING_CARD,
  BILLING_STATUS_EXPIRED,
  BILLING_STATUS_PAYMENT_FAILED,
  FLASH_TYPE,
  IFlash,
  IContactGroupAlert,
  IContactGroupAlertItem,
  IUserSessionData,
  UserState,
  UserType,
} from '../../../store/models/user.model';
import { EReducers, IReducers } from '../../../store/reducers';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { of, Subject } from 'rxjs';
import { DialogService } from '../../ui/dialog/dialog.service';
import { ButtonColor, ButtonSize } from '../../../store/models/button.model';
import { DialogComponent } from '../../ui/dialog/dialog.component';
import { SetSignatureInfoComponent } from '../../../dialogs/set-signature-info/set-signature-info.component';
import { UserService } from '../../../store/services/user.service';
import { IApiModel } from '../../../store/models/api.model';
import { AcceptPolicyComponent } from '../../../dialogs/accept-policy/accept-policy.component';
import { userFlashMessages } from '../../../store/selectors/user.selector';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { WebSocketService } from '../../../store/services/web-socket.service';
import { environment } from '../../../../environments/environment';
import { EventService } from '../../../store/services/event.service';

@Component({
  selector: 'app-authenticated',
  templateUrl: './authenticated.component.html',
  styleUrls: ['./authenticated.component.scss'],
})
export class AuthenticatedComponent implements OnInit, OnDestroy {
  private static readonly REPORT_ISSUE_SCRIPT_ID = 'smote-report-issue-script';
  private static readonly REPORT_ISSUE_SCRIPT_SRC = 'https://snap.smote.sh/w/click2sell.js';

  userSessionData: IUserSessionData;
  contactGroupAlert: IContactGroupAlert | null = null;
  contactGroupAlertExpanded = false;
  showAlert: boolean;
  alertMessage: string;
  leftMenuExpanded = true;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly contactGroupAlertRequest$ = new Subject<void>();

  private policyDialogOpened: boolean;
  private signatureDialogOpened: boolean;
  private billingDialogOpened: boolean;
  billingGraceWarningDismissed = false;

  constructor(
    private router: Router,
    private store: Store<IReducers>,
    private dialogService: DialogService,
    private userService: UserService,
    private sessionService: SessionService,
    private readonly wsService: WebSocketService,
    private readonly eventService: EventService
  ) {
    this.eventService.on<IToggleLeftMenuPayload>(TOGGLE_LEFT_MENU_TYPE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => this.leftMenuExpanded = data.opened);
    this.contactGroupAlertRequest$
      .pipe(
        switchMap(() => {
          if (!this.sessionService.isImpersonating()) {
            return of(null);
          }

          return this.userService.getAlerts('alert').pipe(
            map(response => response?.contact_group_alert ?? null),
            catchError(() => of(null)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(alert => {
        if (!this.sameContactGroupAlert(this.contactGroupAlert, alert)) {
          this.contactGroupAlertExpanded = false;
        }
        this.contactGroupAlert = alert;
      });
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((session: IUserSessionData) => {
        if (session === undefined) {
          return;
        }
        if (session === null) {
          this.contactGroupAlert = null;
          this.contactGroupAlertExpanded = false;
          this.store.dispatch(logout());
          return;
        }

        if(environment.production && session.user_type === UserType.MASTER) {
          this.initReportIssueScript();
        }

        this.userSessionData = session;
        this.contactGroupAlertRequest$.next();
        this.billingGraceWarningDismissed = false;
        const tokenData = localStorage.getItem('token');
        if (tokenData) {
          const token = JSON.parse(tokenData);

          if (!this.wsService.connected) {
            this.wsService.open(token.access_token);
          }
        }
        this.renderRequiredDialogs();
      });
  }

  initReportIssueScript() {
    if (document.getElementById(AuthenticatedComponent.REPORT_ISSUE_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = AuthenticatedComponent.REPORT_ISSUE_SCRIPT_ID;
    script.defer = true;
    script.src = AuthenticatedComponent.REPORT_ISSUE_SCRIPT_SRC;
    document.body.appendChild(script);
  }

  toggleContactGroupAlert(): void {
    this.contactGroupAlertExpanded = !this.contactGroupAlertExpanded;
  }

  get contactGroupAlertSections(): { label: string; detail: string }[] {
    const alert = this.contactGroupAlert;
    if (!alert) {
      return [];
    }

    const sections: { label: string; detail: string }[] = [];
    if (alert.unassigned?.length) {
      sections.push({
        label: 'Not assigned',
        detail: this.formatAlertGroups(alert.unassigned, false),
      });
    }
    if (alert.pending?.length) {
      sections.push({
        label: 'Pending campaigns',
        detail: this.formatAlertGroups(alert.pending, true),
      });
    }
    if (alert.inactive?.length) {
      sections.push({
        label: 'Inactive campaigns',
        detail: this.formatAlertGroups(alert.inactive, true),
      });
    }
    return sections;
  }

  get billingNavigationBlocked(): boolean {
    return (
      !!this.userSessionData &&
      this.userSessionData.billing_status !== BILLING_STATUS_ACTIVE &&
      this.userSessionData.billing_status !== BILLING_STATUS_AWAITING_CARD
    );
  }

  openBillingDetails(): void {
    this.router.navigate(['/accounts', 'update_credit_card']);
  }

  private sameContactGroupAlert(current: IContactGroupAlert | null, next: IContactGroupAlert | null): boolean {
    return JSON.stringify(current) === JSON.stringify(next);
  }

  private formatAlertGroups(groups: IContactGroupAlertItem[], includeCampaign: boolean): string {
    return groups.map(group => {
      const contacts = group.contact_count === 1 ? '1 contact' : `${group.contact_count} contacts`;
      const campaign = includeCampaign && group.campaign_name ? `, ${group.campaign_name}` : '';
      return `${group.group_name} (${contacts}${campaign})`;
    }).join('; ');
  }

  renderRequiredDialogs() {
    if (this.billingDialogOpened || this.policyDialogOpened || this.signatureDialogOpened) {
      return;
    }
    if (this.userSessionData.user_type === UserType.MASTER) {
      const commingFrom = localStorage.getItem('comming_from');

      if (
        commingFrom &&
        (commingFrom === 'login' || commingFrom === 'register')
      ) {
        localStorage.removeItem('comming_from');
        this.router.navigate(['/accounts']);
      }
    }

    if (this.userSessionData.user_type === UserType.MANAGER) {
      const commingFrom = localStorage.getItem('comming_from');

      if (commingFrom && commingFrom === 'verify') {
        this.dialogService.openDialog({
          title: 'Welcome Manager',
          content:
            'Welcome to Retreva!  Your manager account allows you to add users and manage activity. Your manager account comes with one user account.  To add your first user, click "Manage", and then select "Create Account".  Passcodes must be at least six characters.',
          dialogMaxWidth: '300px',
          onClose: () => {
            localStorage.removeItem('comming_from');
            this.store.dispatch(getUserSession());
          },
          buttons: [
            {
              text: 'Ok',
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              actAsClose: true,
            },
          ],
        });

        return;
      }
    }

    if (this.userSessionData.billing_status === BILLING_STATUS_AWAITING_CARD) {
      return;
    }

    if (this.userSessionData.billing_status !== BILLING_STATUS_ACTIVE && !this.billingDialogOpened && window.location.href.indexOf('accounts/update_credit_card') === -1) {
      this.billingDialogOpened = true;
      let content = '';
      switch (this.userSessionData.billing_status) {
        case BILLING_STATUS_EXPIRED:
          content = 'You card has expired. Please, update your payment details';
          break;
        case BILLING_STATUS_PAYMENT_FAILED:
          content = 'The transaction has failed. Please re-confirm you payment details';
          break;
        default:
          content = 'We could not find an active subscription for you account. Please, confirm you payment details';
          break;
      }
      this.store.dispatch(openDialog({
        dialogData: {
          title: 'Payment failed',
          content,
          canClose: false,
          buttons: [
            {
              text: 'Billing details',
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: dialog => {
                this.billingDialogOpened = false;
                dialog.closeDialog();
                this.router.navigate(['/accounts', 'update_credit_card']);
              },
            },
          ],
        },
      }));
      return;
    } else if (!this.userSessionData.user_policy_accepted) {
      if (!this.policyDialogOpened) {
        this.policyDialogOpened = true;
        this.store.dispatch(
          openDialog({
            dialogData: {
              title: 'Acceptable Use Policy, Terms of Use and Privacy Policy',
              content: AcceptPolicyComponent,
              dialogMinHeight: '600px',
              onCloseButton: dialog => {
                dialog.closeDialog();
                this.store.dispatch(logout());
              },
              buttons: [
                {
                  text: 'Accept',
                  color: ButtonColor.GREEN,
                  size: ButtonSize.NORMAL,
                  onClick: (dialog: DialogComponent) => {
                    this.userService
                      .acceptPolicy(this.userSessionData.id)
                      .subscribe(
                        (result: IApiModel) => {
                          this.store.dispatch(getUserSession());
                          dialog.close();
                        },
                        () => dialog.close(),
                      );
                  },
                },
              ],
            },
          }),
        );
      }
    } else if (
      !this.userSessionData.signature &&
      this.router.url.indexOf('account') === -1 &&
      !this.signatureDialogOpened
    ) {
      this.signatureDialogOpened = true;
      this.store.dispatch(
        openDialog({
          dialogData: {
            title: 'Signature',
            content: SetSignatureInfoComponent,
            dialogMaxWidth: '500px',
            buttons: [
              {
                text: 'Ok',
                color: ButtonColor.ORANGE,
                size: ButtonSize.NORMAL,
                onClick: (dialog: DialogComponent) => {
                  dialog.close();
                  this.router.navigate(['/accounts/set_signature']);
                },
              },
            ],
          },
        }),
      );
    }
  }

  dismissBillingGraceWarning() {
    this.billingGraceWarningDismissed = true;
  }

  ngOnInit() {
    this.store
      .select(EReducers.USER)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userState: UserState) => {
        if (!userState.loggedIn) {
          if (environment.production) {
            window.location.href = 'https://retreva.com';
            return;
          }
          this.router.navigate(['/login']);
        }
      });
    this.store
      .select(EReducers.PAGE)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pageState: PageState) => {
        if (pageState.navigateTo) {
          this.router.navigate([pageState.navigateTo]);
        }
      });

    this.store
      .select(userFlashMessages)
      .pipe(takeUntil(this.destroy$))
      .subscribe((flashes: IFlash[]) => {
        if (flashes.length > 0) {
          const flash = flashes[0];

          if (flash.type === FLASH_TYPE.DIALOG) {
            this.dialogService.openDialog(flash.dialogData);
          } else if (flash.type === FLASH_TYPE.ALERT) {
            this.showAlert = true;
            this.alertMessage = flash.alertMessage;
            setTimeout(() => this.closeAlert(), 5000);
          }

          this.store.dispatch(popFlash());
        } else {
          this.closeAlert();
        }
      });
  }

  closeAlert() {
    this.showAlert = false;
    this.alertMessage = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(false);
  }
}
